import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { useTitle } from "../../../hooks/useTitle";
import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import Button from "../../../components/BaseUI/Button";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "../../../components/BaseUI/Modal";
import Lozenge from "../../../components/BaseUI/Lozenge";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import SidebarContainer from "../../../components/CustomUI/SidebarContainer";
import Input from "../../../components/BaseUI/Input";
import Label from "../../../components/BaseUI/Label";
import Pagination from "../../../components/BaseUI/Pagination";
import { FiCheck, FiPlus } from "react-icons/fi";
import { withAuth } from "../../../components/HOC/withAuth";
import ReportViewOptionsPanel from "../Reports/shared/ReportViewOptionsPanel";
import { useReportFilters } from "../Reports/shared/useReportFilters";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import { API_URL } from "../Reports/shared/constants";
import { formatCalendarDateShortTr } from "../../../utils/appTime";
import "../Reports/Reports.scss";

const tableColumns = [
  { name: "Tarih", dataType: "text", id: 1 },
  { name: "Vardiya", dataType: "text", id: 2 },
  { name: "Makine", dataType: "text", id: 3 },
  { name: "Parça", dataType: "text", id: 4 },
  { name: "Üretilen (parça)", dataType: "number", id: 5 },
  { name: "Hurda", dataType: "number", id: 6 },
  { name: "Hurda Grubu", dataType: "text", id: 7 },
  { name: "Durum", dataType: "text", id: 8 },
  { name: "İşlemler", id: 9, sortable: false },
];

const isDraft = (scrapEntry) =>
  scrapEntry && scrapEntry._id && scrapEntry.confirmed === false;

const isConfirmedRow = (scrapEntry) =>
  scrapEntry &&
  scrapEntry._id &&
  (scrapEntry.confirmed === true || scrapEntry.confirmed === undefined);

const ScrapEntry = () => {
  useTitle("Rovion MES - Hurda Girişi");
  const { getToken, getUser } = useAuth();
  const filters = useReportFilters();
  const {
    visibleColumns,
    toggleColumnVisibility,
    resetToDefault,
  } = useColumnVisibility("scrap-entry-list", tableColumns);
  const [committed, setCommitted] = useState(() => ({
    startDate: filters.startDate,
    endDate: filters.endDate,
    machineIds: [...filters.machineIds],
    shiftId: filters.shiftId || "",
  }));
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/scrap-entry",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [machines, setMachines] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [scrapGroups, setScrapGroups] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);

  const [entrySidebar, setEntrySidebar] = useState({
    open: false,
    row: null,
    shift: "",
    scrapGroup: "",
    addRejected: "",
    notes: "",
  });

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    scrapId: null,
    row: null,
  });

  const dismissToast = useCallback(
    (id) => setToasts((p) => p.filter((t) => t.id !== id)),
    [],
  );
  const addToast = useCallback((message, type = "success") => {
    setToasts((p) => [...p, { id: Date.now(), message, type }]);
  }, []);
  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("startDate", committed.startDate);
      params.set("endDate", committed.endDate);
      params.set("page", String(pagination.page));
      params.set("limit", String(pagination.limit));
      if (committed.machineIds.length > 0) {
        params.set(
          "machineIds",
          committed.machineIds.map((id) => String(id)).join(","),
        );
      }
      if (committed.shiftId) {
        params.set("shiftId", String(committed.shiftId));
      }
      const { data } = await axios.get(
        `${API_URL}/api/scrap-entry/daily-rows?${params}`,
        authHeaders(),
      );
      setRows(data.rows || []);
      if (data.pagination) {
        setPagination((prev) => ({
          ...prev,
          page: data.pagination.page ?? prev.page,
          limit: data.pagination.limit ?? prev.limit,
          total: data.pagination.total ?? 0,
          totalPages: data.pagination.totalPages ?? 0,
          hasNextPage: !!data.pagination.hasNextPage,
          hasPrevPage: !!data.pagination.hasPrevPage,
        }));
      }
    } catch {
      addToast("Satırlar yüklenemedi", "danger");
      setRows([]);
      setPagination((prev) => ({
        ...prev,
        total: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPrevPage: false,
      }));
    } finally {
      setLoading(false);
    }
  }, [authHeaders, addToast, committed, pagination.page, pagination.limit]);

  const applyFilters = useCallback(() => {
    setPagination((p) => ({ ...p, page: 1 }));
    setCommitted({
      startDate: filters.startDate,
      endDate: filters.endDate,
      machineIds: [...filters.machineIds],
      shiftId: filters.shiftId || "",
    });
  }, [filters.startDate, filters.endDate, filters.machineIds, filters.shiftId]);

  useEffect(() => {
    (async () => {
      try {
        const [mR, sR, gR] = await Promise.all([
          axios.get(`${API_URL}/api/machines`, authHeaders()),
          axios.get(`${API_URL}/api/shifts`, authHeaders()),
          axios.get(`${API_URL}/api/scrap-entry/helper/scrap-groups`, authHeaders()),
        ]);
        setMachines(mR.data.machines || mR.data || []);
        setShifts(sR.data.shifts || []);
        setScrapGroups(gR.data.scrapGroups || []);
      } catch {}
    })();
  }, [authHeaders]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const openEntrySidebar = (row) => {
    const se = row.scrapEntry;
    const rowShiftId = row.shiftId
      ? String(row.shiftId)
      : se?.shift?._id
        ? String(se.shift._id)
        : se?.shift
          ? String(se.shift)
          : "";
    setEntrySidebar({
      open: true,
      row,
      shift: rowShiftId,
      scrapGroup: "",
      addRejected: "",
      notes: "",
    });
  };

  const closeEntrySidebar = () => {
    setEntrySidebar({
      open: false,
      row: null,
      shift: "",
      scrapGroup: "",
      addRejected: "",
      notes: "",
    });
  };

  const currentSidebarRejected = Math.max(
    0,
    Number(entrySidebar.row?.scrapEntry?.rejected) || 0,
  );

  const openConfirmModal = (row, scrapId) => {
    setConfirmModal({ isOpen: true, scrapId, row });
  };

  const closeConfirmModal = () => {
    setConfirmModal({ isOpen: false, scrapId: null, row: null });
  };

  const saveEntryDraft = async () => {
    const { row } = entrySidebar;
    if (!row) return;
    const addAmount = Math.max(0, Number(entrySidebar.addRejected) || 0);
    const dayTotal =
      row.dayTotalProduced != null ? row.dayTotalProduced : row.totalProduced;
    const currentRejected = Math.max(0, Number(row.scrapEntry?.rejected) || 0);

    if (addAmount <= 0) {
      addToast("Eklenecek hurda adedi giriniz", "danger");
      return;
    }

    if (currentRejected + addAmount > dayTotal) {
      addToast("Toplam hurda vardiya üretimini aşamaz", "danger");
      return;
    }

    if (!entrySidebar.scrapGroup) {
      addToast("Hurda grubu seçiniz", "danger");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        machine: row.machineId,
        shift: entrySidebar.shift || null,
        scrapGroup: entrySidebar.scrapGroup,
        part: row.partKey,
        date: row.date,
        totalProduced: dayTotal,
        rejected: addAmount,
        notes: entrySidebar.notes,
      };
      const se = row.scrapEntry;
      if (se?._id) {
        const { data } = await axios.put(
          `${API_URL}/api/scrap-entry/${se._id}`,
          payload,
          authHeaders(),
        );
        addToast(data.message || "Hurda eklendi — onaylayın");
      } else {
        const { data } = await axios.post(
          `${API_URL}/api/scrap-entry`,
          payload,
          authHeaders(),
        );
        addToast(data.message || "Hurda eklendi — onaylayın");
      }
      closeEntrySidebar();
      fetchRows();
    } catch (err) {
      addToast(err?.response?.data?.message || "Kayıt başarısız", "danger");
    } finally {
      setSaving(false);
    }
  };

  const confirmEntry = async (scrapEntryId) => {
    if (!scrapEntryId) return;
    setConfirmingId(scrapEntryId);
    try {
      await axios.patch(
        `${API_URL}/api/scrap-entry/${scrapEntryId}/confirm`,
        {},
        authHeaders(),
      );
      addToast("Hurda girişi onaylandı");
      closeConfirmModal();
      fetchRows();
    } catch (err) {
      addToast(err?.response?.data?.message || "Onay başarısız", "danger");
    } finally {
      setConfirmingId(null);
    }
  };

  return (
    <AppPage
      title="Hurda Girişi"
      actions={
        <ReportViewOptionsPanel
          filters={filters}
          machines={machines}
          shifts={shifts}
          onApply={applyFilters}
          showTransFilters={false}
          showModel={false}
          columns={tableColumns}
          visibleColumns={visibleColumns}
          onToggleColumn={toggleColumnVisibility}
          onResetColumns={resetToDefault}
        />
      }
    >
      <CardContainer>
        <Table
          columns={tableColumns}
          visibleColumns={visibleColumns}
          loading={loading}
          search={false}
          hideFooter
        >
          {rows.map((r, idx) => {
            const se = r.scrapEntry;
            const draft = isDraft(se);
            const confirmed = isConfirmedRow(se);
            const partLabel =
              r.partName && r.partCode
                ? r.partName
                : r.partCode || (r.partKey === "__none__" ? "—" : r.partKey);
            const partSub =
              r.partName && r.partCode ? (
                <span
                  style={{
                    display: "block",
                    fontSize: "0.75rem",
                    color: "#94a3b8",
                  }}
                >
                  {r.partCode}
                </span>
              ) : null;
            return (
              <Tr
                key={`${r.machineId}-${r.date}-${r.shiftId || "n"}-${r.partKey || "p"}-${idx}`}
                data={r}
              >
                {visibleColumns.includes(1) && (
                  <Td>{formatCalendarDateShortTr(r.date)}</Td>
                )}
                {visibleColumns.includes(2) && (
                  <Td>
                    <span style={{ fontWeight: 600 }}>{r.shiftName || "—"}</span>
                  </Td>
                )}
                {visibleColumns.includes(3) && (
                  <Td>
                    <strong>{r.machineName}</strong>
                    {r.machineCode && (
                      <span
                        style={{
                          marginLeft: 6,
                          fontSize: "0.75rem",
                          color: "#94a3b8",
                        }}
                      >
                        ({r.machineCode})
                      </span>
                    )}
                  </Td>
                )}
                {visibleColumns.includes(4) && (
                  <Td>
                    <span style={{ fontWeight: 600 }}>{partLabel}</span>
                    {partSub}
                  </Td>
                )}
                {visibleColumns.includes(5) && (
                  <Td>
                    <span style={{ fontWeight: 600 }}>{r.totalProduced}</span>
                    {r.dayTotalProduced != null &&
                      r.dayTotalProduced !== r.totalProduced && (
                        <span
                          style={{
                            display: "block",
                            fontSize: "0.7rem",
                            color: "#94a3b8",
                          }}
                        >
                          Vardiya toplamı: {r.dayTotalProduced}
                        </span>
                      )}
                  </Td>
                )}
                {visibleColumns.includes(6) && (
                  <Td>
                    <span
                      style={{
                        color: (se?.rejected || 0) > 0 ? "#ef4444" : "#64748b",
                        fontWeight: 600,
                      }}
                    >
                      {se ? (se.rejected ?? 0) : "—"}
                    </span>
                  </Td>
                )}
                {visibleColumns.includes(7) && (
                  <Td>
                    {se?.scrapGroup?.name ? (
                      <Lozenge appearance="default-subtle">{se.scrapGroup.name}</Lozenge>
                    ) : (
                      "—"
                    )}
                  </Td>
                )}
                {visibleColumns.includes(8) && (
                  <Td>
                    {!se?._id && (
                      <Lozenge appearance="default-subtle">Kayıt yok</Lozenge>
                    )}
                    {se?._id && draft && (
                      <Lozenge appearance="warning-subtle" isBold>
                        Taslak
                      </Lozenge>
                    )}
                    {se?._id && confirmed && (
                      <Lozenge appearance="success-subtle" isBold>
                        Onaylı
                      </Lozenge>
                    )}
                  </Td>
                )}
                {visibleColumns.includes(9) && (
                  <Td>
                    <div className="d-flex align-items-center justify-content-start gap-2 flex-wrap">
                      {canWrite && (
                        <FiPlus
                          size="16"
                          onClick={() => openEntrySidebar(r)}
                          style={{ cursor: "pointer" }}
                        />
                      )}
                      {canWrite && se?._id && draft && (
                        <FiCheck
                          size="16"
                          onClick={() => openConfirmModal(r, se._id)}
                          style={{ cursor: "pointer" }}
                          disabled={confirmingId === se._id}
                        />
                      )}
                    </div>
                  </Td>
                )}
              </Tr>
            );
          })}
        </Table>

        {!loading && pagination.total > 0 && (
          <div className="mt-3">
            <Pagination
              totalItems={pagination.total}
              totalPerPage={pagination.limit}
              currentPage={pagination.page}
              totalRecords={pagination.total}
              onPageChange={({ pagination: paginationData }) => {
                setPagination((prev) => ({
                  ...prev,
                  page: paginationData.currentPage,
                  limit: paginationData.itemsPerPage,
                }));
              }}
              options={{
                skipPrevious: true,
                previous: true,
                next: true,
                skipNext: true,
                selectQuantity: true,
                simpleCounting: false,
              }}
            />
          </div>
        )}
      </CardContainer>

      <SidebarContainer
        show={entrySidebar.open}
        title="Hurda girişi"
        onClose={closeEntrySidebar}
      >
        {entrySidebar.row && (
          <div className="d-flex flex-column" style={{ height: "100%" }}>
            <div className="d-flex flex-column gap-3 flex-grow-1">
              <CardContainer
                title="Kayıt bilgisi"
                className="scrap-entry-sidebar-info-content"
              >
                <div className="scrap-entry-sidebar-info-body">
                  <div>
                    <strong>{entrySidebar.row.machineName}</strong>
                    {entrySidebar.row.machineCode
                      ? ` (${entrySidebar.row.machineCode})`
                      : ""}
                  </div>
                  <div className="text-muted">
                    {formatCalendarDateShortTr(entrySidebar.row.date)}
                    {entrySidebar.row.shiftName
                      ? ` · ${entrySidebar.row.shiftName}`
                      : ""}
                  </div>
                  <div className="text-muted small">
                    Parça:{" "}
                    <strong>
                      {entrySidebar.row.partName && entrySidebar.row.partCode
                        ? `${entrySidebar.row.partName} (${entrySidebar.row.partCode})`
                        : entrySidebar.row.partCode ||
                          (entrySidebar.row.partKey === "__none__"
                            ? "—"
                            : entrySidebar.row.partKey)}
                    </strong>
                  </div>
                  <div>
                    Bu parça: <strong>{entrySidebar.row.totalProduced}</strong>
                    {entrySidebar.row.dayTotalProduced != null &&
                      entrySidebar.row.dayTotalProduced !==
                        entrySidebar.row.totalProduced && (
                        <>
                          {" · "}
                          <span>
                            Vardiya toplamı (hurda limiti):{" "}
                            <strong>{entrySidebar.row.dayTotalProduced}</strong>
                          </span>
                        </>
                      )}
                  </div>
                  <div>
                    Mevcut hurda: <strong>{currentSidebarRejected}</strong>
                  </div>
                </div>
              </CardContainer>
              <div>
                <Label>Hurda Grubu *</Label>
                <select
                  value={entrySidebar.scrapGroup}
                  onChange={(e) =>
                    setEntrySidebar((p) => ({ ...p, scrapGroup: e.target.value }))
                  }
                  style={{
                    width: "100%",
                    height: 38,
                    padding: "0 10px",
                    border: "1px solid #d0d5dd",
                    borderRadius: 6,
                    fontSize: 13,
                    background: "var(--m-card-background-color, #fff)",
                    color: "inherit",
                  }}
                >
                  <option value="">— Hurda Grubu Seçiniz —</option>
                  {scrapGroups
                    .filter((g) => g.isActive !== false)
                    .map((g) => (
                      <option key={g._id} value={g._id}>
                        {g.name}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <Label>Eklenecek Hurda (adet)</Label>
                <Input
                  type="number"
                  min="1"
                  value={entrySidebar.addRejected}
                  onChange={(v) =>
                    setEntrySidebar((p) => ({ ...p, addRejected: v }))
                  }
                />
              </div>
              <div>
                <Label>Not</Label>
                <textarea
                  value={entrySidebar.notes}
                  onChange={(e) =>
                    setEntrySidebar((p) => ({ ...p, notes: e.target.value }))
                  }
                  rows={2}
                  style={{
                    width: "100%",
                    padding: "7px 10px",
                    border: "1px solid #d0d5dd",
                    borderRadius: 6,
                    fontSize: 13,
                    background: "var(--m-card-background-color, #fff)",
                    color: "inherit",
                    resize: "vertical",
                  }}
                />
              </div>
            </div>
            <div className="mt-auto pt-3 d-flex flex-row-reverse gap-2">
              <Button
                appearance="primary-animated"
                onClick={saveEntryDraft}
                disabled={saving || !canWrite}
                className="w-100"
              >
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </Button>
              <Button
                appearance="danger-subtle"
                onClick={closeEntrySidebar}
                className="w-100"
              >
                İptal
              </Button>
            </div>
          </div>
        )}
      </SidebarContainer>

      <Modal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirmModal}
        width="small"
      >
        <ModalHeader>Hurda girişini onayla</ModalHeader>
        <ModalBody>
          {confirmModal.row && (
            <>
              <p className="sub-title-page mb-3">
                <strong>{confirmModal.row.machineName}</strong>
                {confirmModal.row.machineCode
                  ? ` (${confirmModal.row.machineCode})`
                  : ""}
                <br />
                {formatCalendarDateShortTr(confirmModal.row.date)}
                {confirmModal.row.shiftName
                  ? ` · ${confirmModal.row.shiftName}`
                  : ""}
                <br />
                Hurda:{" "}
                <strong>
                  {confirmModal.row.scrapEntry?.rejected ?? 0}
                </strong>{" "}
                adet
              </p>
              <p className="text-muted small mb-0">
                Taslak hurda kaydını onaylamak üzeresiniz. İptal ile
                vazgeçebilirsiniz.
              </p>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button appearance="subtle" onClick={closeConfirmModal}>
            İptal
          </Button>
          <Button
            appearance="primary-animated"
            disabled={
              !confirmModal.scrapId || confirmingId === confirmModal.scrapId
            }
            onClick={() => confirmEntry(confirmModal.scrapId)}
          >
            {confirmingId === confirmModal.scrapId ? "Onaylanıyor…" : "Onayla"}
          </Button>
        </ModalFooter>
      </Modal>

      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.message}
            appearance={t.type === "error" ? "danger" : t.type || "success"}
            onClose={() => dismissToast(t.id)}
          />
        ))}
      </ToastContainer>
    </AppPage>
  );
};

export default withAuth(ScrapEntry);
